import { VStack } from "@chakra-ui/layout"
import React, { useMemo } from "react"
import { Control, Controller, useController } from "react-hook-form"

import { GetAuthRegister } from "~/api/auth/register/get"
import Selectbox from "~/components/atoms/Selectbox"
import { FormContainer } from "~/components/molecules/FormContainer"
import { FormSelectboxForOccupations } from "~/components/molecules/FormSelectboxForOccupations"
import SelectboxMultiple from "~/components/molecules/SelectboxMultiple"
import { SpacingSize } from "~/consts/style-mixins"
import { createOptions } from "~/services/createOptions"

import { Section } from "../components/Section"
import { LABELS, VALIDATE_MAP } from "../constants"
import { HackedPostAuthRegisterBody } from "../types"

type Props = {
  control: Control<HackedPostAuthRegisterBody>
  formMasterData: GetAuthRegister
}

export const NewGradJobs: React.VFC<Props> = ({ control, formMasterData }) => {
  const errors = control._formState.errors

  const options = useMemo(
    () => ({
      jobHuntingStatuses: createOptions(formMasterData.job_hunting_statuses, { isAddInitialOption: true }),
      industries: createOptions(formMasterData.industries),
      companyTypes: createOptions(formMasterData.company_types),
    }),
    [formMasterData]
  )

  const controllerForMajorOccupation = useController({
    control,
    name: "major_occupation_ids_for_new_grad_jobs",
    shouldUnregister: true,
  })
  const { value: selectedForMajorOccupation, onChange: onChangeForMajorOccupation } = controllerForMajorOccupation.field

  const controllerForMinorOccupation = useController({
    control,
    name: "minor_occupation_ids_for_new_grad_jobs",
    shouldUnregister: true,
  })
  const { value: selectedForMinorOccupation, onChange: onChangeForMinorOccupation } = controllerForMinorOccupation.field

  return (
    <Section title="就職活動について">
      <VStack spacing={SpacingSize._2XL}>
        <FormContainer label={LABELS.jobHuntingStatus} errorMessage={errors.job_hunting_status?.message}>
          <Selectbox
            options={options.jobHuntingStatuses ?? []}
            selectProps={control.register("job_hunting_status")}
            isError={!!errors.job_hunting_status}
          />
        </FormContainer>

        <FormSelectboxForOccupations
          occupations={formMasterData?.occupations ?? []}
          selectedForMajorOccupationIds={selectedForMajorOccupation ?? []}
          selectedForMinorOccupationIds={selectedForMinorOccupation ?? []}
          onChangeForMajorOccupationIds={onChangeForMajorOccupation}
          onChangeForMinorOccupationIds={onChangeForMinorOccupation}
          error={errors.major_occupation_ids_for_new_grad_jobs?.message}
        />

        <FormContainer label={LABELS.industryIds} errorMessage={errors.industry_ids?.message}>
          <Controller
            name="industry_ids"
            control={control}
            shouldUnregister
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SelectboxMultiple
                label={LABELS.industryIds}
                options={options.industries ?? []}
                selected={value ?? []}
                onChange={onChange}
                error={!!error}
              />
            )}
          />
        </FormContainer>

        <FormContainer label={LABELS.companyTypeIds} errorMessage={errors.company_type_ids?.message}>
          <Controller
            name="company_type_ids"
            control={control}
            shouldUnregister
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SelectboxMultiple
                label={LABELS.companyTypeIds}
                options={options.companyTypes ?? []}
                selected={value ?? []}
                onChange={onChange}
                error={!!error}
              />
            )}
          />
        </FormContainer>
      </VStack>
    </Section>
  )
}
